// eslint-disable-next-line no-restricted-imports
import { Suspense, lazy, useEffect, useState } from 'react';
import { Box, Flex, Spinner, Tabs } from '@applyboard/crystal-ui';
import {
  BookFillIcon,
  CreditCardFillIcon,
  GridFillIcon,
  PersonFillIcon,
  SvgIconProps,
} from '@applyboard/ui-icons';
import { I18nextProvider, useEventTracker, useSetPageMeta } from '@applyboard/ui-utils';
import ThreeSixtySolutionsDesktopLogo from '../../assets/ThreeSixtySolutionsDesktopLogo.png';
import ThreeSixtySolutionsMobileLogo from '../../assets/ThreeSixtySolutionsMobileLogo.png';
import { InProductFeedBackDialog } from '../../components/InProductFeedBack';
import { default as Notification } from '../../components/Notification/Notification';
import { useInitI18n } from '../../i18n';
const ServiceListingsLazy = lazy(() => import('./ServiceListings'));
const StudentListingLazy = lazy(() => import('./StudentListings/StudentListing'));
const TestVouchersDashboardLazy = lazy(() => import('../TestVouchers/TestVouchersDashboard'));
const ResourcesLazy = lazy(() => import('./Resources/Resources'));
import { WhatsNewButtonModal } from './WhatsNewButtonModal';

// Query param key for top-level VAS Hub tab
const VAS_HUB_TAB_QUERY_PARAM = 'hub_tab';

const FOR_TESTING = 'for_testing';

const HUB_TABS: {
  key: string;
  icon: (value: Omit<SvgIconProps, 'children'>) => JSX.Element;
  Component: React.LazyExoticComponent<() => JSX.Element>;
}[] = [
  { key: 'Services', icon: GridFillIcon, Component: ServiceListingsLazy },
  { key: 'Students', icon: PersonFillIcon, Component: StudentListingLazy },
  {
    key: 'Test Vouchers',
    icon: CreditCardFillIcon,
    Component: TestVouchersDashboardLazy,
  },
  { key: 'Resources', icon: BookFillIcon, Component: ResourcesLazy },
];

const HUB_TAB_KEYS = HUB_TABS.map(tab => tab.key);
const isHubTab = (val: string) => HUB_TAB_KEYS.includes(val);
type HubTab = (typeof HUB_TAB_KEYS)[number];
const DEFAULT_HUB_TAB: HubTab = 'Services';

const getInitialHubTab = (): HubTab => {
  const params = new URLSearchParams(window.location.search);
  const qp = params.get(VAS_HUB_TAB_QUERY_PARAM);
  return qp && isHubTab(qp) ? qp : DEFAULT_HUB_TAB;
};

const VasHubPage = () => {
  const { trackEvent } = useEventTracker();
  const [activeTab, setActiveTab] = useState<HubTab>(getInitialHubTab);
  const params = new URLSearchParams(window.location.search);
  const isTesting = params.get(FOR_TESTING) === 'true';
  const i18n = useInitI18n();

  useSetPageMeta({ title: '360 Solutions' });

  useEffect(() => {
    trackEvent({ event: 'VAS - VAS Hub Page - Page View' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (value: string) => {
    const tabSelected = isHubTab(value) ? value : DEFAULT_HUB_TAB;
    setActiveTab(tabSelected);
    trackEvent({
      event: 'VAS - VAS Hub Page - Tab Changed',
      data: { tab: tabSelected },
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set(VAS_HUB_TAB_QUERY_PARAM, activeTab);
    const newRelativePath = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newRelativePath);
  }, [activeTab]);

  if (!i18n) {
    return (
      <Flex align="center" justify="center" p={10}>
        <Spinner size="xl" />
      </Flex>
    );
  }

  const suspenseFallback = (
    <Flex align="center" justify="center" p={10}>
      <Spinner size="xl" />
    </Flex>
  );

  return (
    <I18nextProvider i18n={i18n}>
      <Box p={6}>
        {/* Header Section */}
        <Box mb={6}>
          <Flex justify="between" align="center" wrap={true} gap={2}>
            <Box>
              <Box hideBelow="sm">
                <img
                  src={ThreeSixtySolutionsDesktopLogo}
                  alt="360 Solutions"
                  height="50px"
                  width="auto"
                />
              </Box>
              <Box hideAbove="sm">
                <img
                  src={ThreeSixtySolutionsMobileLogo}
                  alt="360 Solutions"
                  height="50px"
                  width="auto"
                />
              </Box>
            </Box>
            <Flex direction="row" gap={4} justify="start" align="center">
              <WhatsNewButtonModal maxItems={3} />
              <InProductFeedBackDialog />
              <Notification />
            </Flex>
          </Flex>
        </Box>

        {/* Tabbed Content */}
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="VAS Hub sections">
          {HUB_TABS.filter(tab => isTesting || tab.key !== 'Resources').map(
            ({ key, icon: Icon, Component }) => (
              <Tabs.Item key={key} value={key} label={key} icon={Icon}>
                {activeTab === key ? (
                  <Suspense fallback={suspenseFallback}>
                    <Component />
                  </Suspense>
                ) : null}
              </Tabs.Item>
            ),
          )}
        </Tabs>
      </Box>
    </I18nextProvider>
  );
};

export default VasHubPage;
